import type { Observable } from 'rxjs';
import type { SitePageEntity } from '@common/types/site-page.type';
import type { Domain } from '@common/types/domain.type';

import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { AsyncPipe } from '@angular/common';

import type { FormAdditionalSettingsData } from '@sites/entity-core';
import type { FormWidgetForm } from '@sites/entity-signal-form';

import { PagesFacade } from '@web/store/website/pages/pages.facade';
import { DomainsFacade } from '@web/store/website/domains/domains.facade';
import { LayoutFacade } from '@web/store/layout/layout.facade';

import { environment } from '@web-env/environment';
import { CommonElementsModule } from '@web/widgets/common/common-elements.module';
import { SpFormAdditionalSettingsSignalComponent } from '@components/sp-form-additional-settings-signal';

import { FormSettingsPanel } from '@web/widgets/common/abstract/form-settings-panel';

/**
 * Breadcrumbs/footer chrome around `sp-form-additional-settings-signal`,
 * writing straight into the widget signal form. Save/cancel and the
 * transaction frame come from `FormSettingsPanel`.
 */
@Component({
    selector: 'web-form-additional-settings',
    templateUrl: './form-additional-settings.component.html',
    styleUrls: ['./form-additional-settings.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonElementsModule, SpFormAdditionalSettingsSignalComponent, AsyncPipe],
})
export class FormAdditionalSettingsComponent extends FormSettingsPanel<FormWidgetForm> {
    public readonly addChatBotToForm = input<boolean>(false);

    private readonly pagesFacade: PagesFacade = inject(PagesFacade);
    private readonly domainsFacade: DomainsFacade = inject(DomainsFacade);
    private readonly layoutFacade: LayoutFacade = inject(LayoutFacade);

    public readonly pages$: Observable<SitePageEntity[]> = this.pagesFacade.pages$;
    public readonly domains$: Observable<Domain[]> = this.domainsFacade.domains$;

    public readonly baseHref = environment.baseHref;
    public readonly selectedDeviceMode$ = this.layoutFacade.deviceMode$;

    public readonly settings = computed<FormAdditionalSettingsData>(() => this.form().additionalFormSettings().value());

    public onSettingsChange(value: FormAdditionalSettingsData): void {
        this.form().setAdditionalFormSettings(value);
    }
}
